<template>
  <v-navigation-drawer
    v-model="computedValue"
    :location="location"
    elevation="1"
    temporary
    v-bind="$attrs"
    width="540"
  >
    <div class="pane-wrapper">
      <div class="pane-header-prepend">
        <nav class="d-flex align-center mr-auto" v-if="canNavigate">
          <AppTooltip :disabled="!navigatePrevious">
            <template #activator="{ props }">
              <app-button :disabled="!navigatePrevious"
                          data-locator="previous-item-nav-button" 
                          type="icon"
                          v-bind="props"
                          @click="onPreviousItem"
              >
                <app-icon icon-name="chevron-up" size="small" />
              </app-button>
            </template>
            {{ tooltipNavigatePrevious }}
          </AppTooltip>
          <AppTooltip :disabled="!navigateNext">
            <template #activator="{ props }">
              <app-button :disabled="!navigateNext"
                          data-locator="next-item-nav-button"
                          type="icon" 
                          v-bind="props"
                          @click="onNextItem"
              >
                <app-icon icon-name="chevron-down" size="small" />
              </app-button>
            </template> 
            {{ tooltipNavigateNext }}
          </AppTooltip>
        </nav>
        <app-button class="close-button" data-locator="close-button" type="icon" @click="onClosePane">
          <app-icon icon-name="xmark" icon-weight="far" />
        </app-button>
      </div>
      <div class="pane-header">
        <slot name="header"></slot>
      </div>
      <template v-if="hasTabs">
        <v-toolbar flat dense height="0" color="transparent">
          <template #extension>
            <v-tabs v-model="computedCurrentTab"
                    slider-color="transactional"
            >
              <v-tab
                v-for="tab in tabs"
                :value="tab.id"
                :key="tab.id"
                :data-locator="`${tab.id}-tab`"
                :ripple="false"
              >
                <span>{{tab.title}}</span>
              </v-tab>
            </v-tabs>
          </template>
        </v-toolbar>
      </template>
      <div class="pane-content">
        <slot name="content">
          <v-window v-model="computedCurrentTab"  v-if="hasTabs">
            <v-window-item
              v-for="tab in tabs"
              :key="tab.id"
              :value="tab.id"
              eager
            >
              <!-- @slot Dynamic slots for the content of the tabs. Starts with "content-" -->
              <slot :name="`content-${tab.id}`"></slot>
            </v-window-item>
          </v-window>
        </slot>
      </div>
      <slot name="footer"></slot>
    </div>
  </v-navigation-drawer>
</template>

<script>
import AppTooltip from '@/common/AppTooltip.vue'

export default {
  name: 'AppPane',
  components: { AppTooltip },
  emits: ['close-pane', 'next-item', 'previous-item', 'update:modelValue', 'update:currentTab'],
  inheritAttrs: false,
  props: {
    currentTab: {
      type: [String, null],
      default: null,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    location: {
      type: String,
      default: 'right',
      validator (value) {
        return ['right', 'left'].includes(value)
      },
    },
    navigateNext: {
      type: [Object, null],
      default: null,
    },
    navigateNextLabel: {
      type: String,
      default: '',
    },
    navigatePrevious: {
      type: [Object, null],
      default: null,
    },
    navigatePreviousLabel: {
      type: String,
      default: '',
    },
    tabs: {
      type: Array,
      required: true,
    },
  },

  computed: {
    canNavigate() {
      return this.navigatePrevious || this.navigateNext
    },
    hasTabs() {
      return this.tabs.length > 0
    },
    computedValue: {
      get () {
        return this.isOpen
      },
      set (value) {
        this.$emit('update:modelValue', value)
      },
    },
    computedCurrentTab: {
      get () {
        return this.currentTab
      },
      set (value) {
        this.$emit('update:currentTab', value)
      },
    },
    tooltipNavigateNext() {
      return this.navigateNextLabel || this.$t('common.next')
    },
    tooltipNavigatePrevious() {
      return this.navigatePreviousLabel || this.$t('common.previous')
    },
  },
  methods: {
    onClosePane () {
      this.computedValue = false
      this.$emit('close-pane')
    },
    onNextItem() {
      this.$emit('next-item')
    },
    onPreviousItem() {
      this.$emit('previous-item')
    },
  },
}
</script>

<style lang="scss" scoped>
.pane-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  .pane-header {
    padding: 12px 0;
  }

  .pane-header-prepend {
    align-items: center;
    display: flex;
    justify-content: end;
    padding: 16px 16px 0;
  }
}

.pane-content {
  overflow-y: auto;
  height: 100%;
}

:deep(.v-window) {
  &, .v-window__container, .v-window-item--active {
    height: 100%;
    overflow-y: auto;
  }
}

:deep(.v-toolbar) {
  border-bottom: 1px solid rgb(var(--v-theme-grey-lighten-4));
}

:deep(.v-tab.v-btn) {
  padding: 0 4px;

  .v-btn__overlay, 
  .v-btn__underlay {
    display: none;
  }

  &.v-tab--selected {
    font-weight: 500;
    color:  rgb(var(--v-theme-grey-dark)) !important;
  }

  .v-btn__content {
    border-radius: 8px;
    padding: 8px;
    transition: all 0.25s ease;
  }

  &:hover:not(.v-tab--selected) .v-btn__content {
    background-color: rgb(var(--v-theme-grey-lighten-5));
  }
}
</style>